import type { ChaveDaSecao, SecaoDosAutos } from '../types/baixarProcesso.types';

/** A ordem fixa do servidor — a marcação a preserva para o resumo bater com o documento. */
export const CHAVES_DA_SECAO = [
  'informacoesGerais',
  'partes',
  'autos',
  'mensagens',
  'certificadoAcessos',
  'certificadoSentenca',
] as const satisfies readonly ChaveDaSecao[];

/**
 * O título vem do servidor; a descrição é daqui e não traz contagem: número de eventos ou de
 * mensagens fixo no texto descrevia o processo do mock, não o que está aberto.
 */
export const DESCRICAO_DA_SECAO: Record<ChaveDaSecao, string> = {
  informacoesGerais: 'Resumo, descrição do pedido, rito e valor',
  partes: 'Requerente, requerida, advogados e árbitra',
  autos: 'Eventos em ordem cronológica, com índice e anexos',
  mensagens: 'Mensagens registradas fora dos eventos',
  certificadoAcessos: 'Ciência das partes sobre a citação, por canal',
  certificadoSentenca: 'Ciência das partes sobre a sentença arbitral',
};

/** Por que a seção não pode entrar agora — o servidor diz que não, a tela diz o motivo. */
export const MOTIVO_DA_SECAO_INDISPONIVEL: Record<ChaveDaSecao, string> = {
  informacoesGerais: 'Indisponível neste processo',
  partes: 'Indisponível neste processo',
  autos: 'Ainda não há eventos nos autos',
  mensagens: 'Sem mensagens no chat',
  certificadoAcessos: 'Ainda não há certificado de acessos',
  // "Ainda não há" mentia quando a sentença existiu e foi cancelada: o servidor só conta a vigente.
  certificadoSentenca: 'Não há sentença vigente no processo',
};

/**
 * "A, B e C" — o resumo lê como frase, não como lista técnica.
 *
 * @example listaEmPortugues(['Partes', 'Autos', 'Mensagens']) // 'Partes, Autos e Mensagens'
 */
export function listaEmPortugues(itens: readonly string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/**
 * O aviso da revisão depois de uma seção marcada sumir entre a seleção e a confirmação, com o
 * nome de quem saiu.
 *
 * @example avisoDeSecoesQueSairam(['Certificado de sentença'])
 */
export function avisoDeSecoesQueSairam(titulos: readonly string[]): string {
  const plural = titulos.length > 1;
  return `${listaEmPortugues(titulos)} ${plural ? 'deixaram' : 'deixou'} de estar ${
    plural ? 'disponíveis' : 'disponível'
  } antes da confirmação e ${plural ? 'saíram' : 'saiu'} da seleção. Revise e baixe de novo.`;
}

export function chavesDisponiveis(secoes: readonly SecaoDosAutos[]): ChaveDaSecao[] {
  return secoes.filter((secao) => secao.disponivel).map((secao) => secao.chave);
}

/** Mantém a ordem do documento, qualquer que seja a ordem em que as chaves foram marcadas. */
export function naOrdemDoDocumento(chaves: readonly ChaveDaSecao[]): ChaveDaSecao[] {
  return CHAVES_DA_SECAO.filter((chave) => chaves.includes(chave));
}

/**
 * A seleção vale o documento completo quando leva tudo o que está disponível: é o que o servidor
 * devolve nesse caso (`secoes: null`).
 *
 * @example selecaoEhCompleta(['partes'], ['partes']) // true
 */
export function selecaoEhCompleta(
  marcadas: readonly ChaveDaSecao[],
  disponiveis: readonly ChaveDaSecao[],
): boolean {
  return disponiveis.length > 0 && disponiveis.every((chave) => marcadas.includes(chave));
}
